import React, { useCallback } from "react";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import pxToRem from "~libs/pxToRem";
import { makeStyles } from "@mui/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import ItemLoadingCard from "~business/Cards/ItemLoadingCard";
import IconEdit from "~assets/icons/ic_edit.svg";
import useField from "~hooks/useField";

type ValueChangePayload = {
  value: string;
  valid: boolean;
  focus: boolean;
};

export interface ItemInputCardProps {
  id: string;
  label: string;
  value: string;
  onChange?: (payload: ValueChangePayload) => void;
  placeholder?: string;
  required?: boolean;
  displayError?: boolean;
  focus?: boolean;
  onBlur?: () => void;
  readOnly?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  variant?: "big" | "small" | string;
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2%",
    borderBottom: "1px solid #8B8B8B",
  },
  caption: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: 600,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "left",
    color: "#8B8B8B",
  },
  body: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    maxWidth: pxToRem(270),
    width: "50%",
  },
  value: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: "bold",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "right",
    color: "#424242",
  },
  input: {
    padding: "5%",
    textAlign: "end",
    width: "100%",
    "& .Mui-error": {
      borderRadius: "4px",
      border: "solid 1.7px #f77221",
    },
    "&:Mui-error": {
      borderRadius: "4px",
      border: "solid 1.7px #f77221",
    },
  },
  buttonLink: {
    display: "flex",
    alignItems: "center",
    color: "#f77221",
    paddingLeft: 8,
    cursor: "pointer",
  },
  icon: {
    width: 24,
    height: 24,
  },
}));

const errorText = "Requerido";

const ItemInputCard: React.FC<ItemInputCardProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  required = false,
  displayError = false,
  focus = false,
  onBlur,
  readOnly = false,
  isLoading = false,
  onClick,
}) => {
  const classes = useStyles();

  const validateFunction = useCallback(
    (val: string) => handleValidate(required)(val),
    [required]
  );

  const [localValue, setValue, , reference] = useField(
    value,
    validateFunction,
    focus,
    isLoading,
    readOnly
  );

  const handleBlur = () => {
    onBlur?.();
  };

  const handleChangeValue = (next: string) => {
    setValue((prev) => ({ ...prev, value: next }));
    onChange?.({
      value: next,
      valid: validateFunction(next),
      focus: false,
    });
  };

  const showError = !localValue.valid && displayError;

  if (isLoading) return <ItemLoadingCard label={label} />;

  return (
    <div className={classes.container}>
      <Typography className={classes.caption}>{label}</Typography>
      <div className={classes.body}>
        {localValue.editing ? (
          <TextField
            id={id}
            style={{ width: "100%" }}
            inputProps={{ className: classes.input }}
            onChange={(event) => handleChangeValue(event.target.value)}
            onBlur={handleBlur}
            value={localValue.value}
            variant="outlined"
            placeholder={placeholder}
            error={showError}
            inputRef={reference}
          />
        ) : (
          <>
            {value ? (
              <Typography className={classes.value}>{value}</Typography>
            ) : (
              <div>&mdash;</div>
            )}
            {!readOnly && (
              <div className={classes.buttonLink} onClick={() => onClick?.()}>
                <img alt="" src={IconEdit} className={classes.icon} />
              </div>
            )}
          </>
        )}
        {!localValue.valid && (
          <Tooltip title={errorText}>
            <div className={classes.buttonLink}>
              <InfoOutlinedIcon style={{ width: 30, height: 30 }} />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default ItemInputCard;

const handleValidate =
  (required?: boolean) =>
  (val: string): boolean => {
    const isDataValid = !required || !!val;
    return isDataValid;
  };
